'use client';

import { useRef, useState } from 'react';
import ProductCard from './ProductCard';

// BL-815: below lg the cards are a snap carousel, from lg up the three-column
// grid this block has always been. One element switches between the two rather
// than rendering a mobile tree and a desktop tree: the cards are identical at
// both breakpoints, and ProductCard calls usePricing(), so duplicating them
// would run that work twice and ship two copies of every card to the DOM.
//
// Geometry measured off birchliving.com — production returns 403 to automated
// access, so it comes from 2x screenshots at a stated viewport rather than
// anything this repo can fetch:
//
//   viewport 390   gutter 40   card 310   gap 20   neighbour 20
//   viewport 440   gutter 40   card 360   gap 20   neighbour 20
//
// The gutter and the gap are fixed and the card absorbs the difference, which
// is just `w-full` inside a container padded by 40 — no calc needed. Scrolled
// to the middle card at 440 it reads 19 / 20 / 360 / 20 / 19, symmetric, so
// production centres rather than snapping to the start.
//
// Two earlier passes got this wrong and both are worth remembering. The first
// centred a card at 75% of the viewport, which squeezed the price and CTA. The
// second read the geometry off a compressed video frame whose width I had
// estimated by eye, and came out at 90% behind a 12px gutter — too wide, and
// left-aligned on the strength of a frame that could not actually show the
// difference. The screenshots settle both: a 2x capture with the viewport
// stated in DevTools beats a frame grab every time.

const ProductCardsRow = ({ cards, label }) => {
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  // Which card is centred, derived from scroll position — there is no discrete
  // "slide changed" event behind a native scroll-snap container.
  const syncActive = () => {
    const el = scrollRef.current;
    if (!el || cards.length < 2) return;
    const step = (el.scrollWidth - el.clientWidth) / (cards.length - 1);
    if (step <= 0) return;
    const index = Math.round(el.scrollLeft / step);
    setActive(Math.max(0, Math.min(cards.length - 1, index)));
  };

  return (
    <>
      {/* Focusable so the carousel can be scrolled from the keyboard, labelled
          so it announces as something rather than an unnamed group. The focus
          ring is not decoration: a focusable div with no visible indicator
          trades a 2.1.1 failure for a 2.4.7 one. Same treatment as the
          Instagram feed's carousel. */}
      <div
        ref={scrollRef}
        onScroll={syncActive}
        tabIndex={0}
        role="region"
        aria-label={label}
        // -mx-4 cancels Container's padding so the row spans the viewport, and
        // px-10/scroll-px-10 put production's own 40px gutter back. Everything
        // above lg is reset, where the three-column grid takes over unchanged.
        //
        // Snapping is mandatory on touch, where each gesture is one flick and
        // landing on a card is what makes it feel right, but proximity on a
        // fine pointer: a trackpad emits a stream of small scroll events, and
        // mandatory re-snaps on every one, which reads as the carousel yanking
        // itself card to card instead of gliding. No `scroll-smooth` — it only
        // affects programmatic scrolls, of which this component has none.
        className="-mx-4 flex snap-x snap-mandatory items-stretch gap-5 overflow-x-auto overscroll-x-contain px-10 pb-2 scroll-px-10 [@media(pointer:fine)]:snap-proximity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sea lg:mx-0 lg:grid lg:grid-cols-3 lg:gap-8 lg:overflow-x-visible lg:px-0 lg:pb-0"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {cards.map((card, i) => (
          // `grid`, not `flex`. A lone grid child stretches on both axes by
          // default, which is what the card needs: full height for the row and
          // full width of its track. As a flex item it sized to its content
          // instead, leaving each card short of its 480px track by a different
          // amount — 432 / 425 / 368, widening with the viewport.
          <div
            key={i}
            className="grid w-full shrink-0 snap-center lg:w-auto lg:shrink"
          >
            <ProductCard card={card} />
          </div>
        ))}
      </div>

      {/* One continuous sand bar with the active third in sea, as production and
          Helix draw it: the segments carry no gap, so they read as a single
          track rather than three dashes. Decorative — it mirrors a position the
          scroll container already exposes to assistive tech, and production
          gives it no interaction either. */}
      {cards.length > 1 && (
        <div aria-hidden="true" className="mt-6 flex lg:hidden">
          {cards.map((card, i) => (
            <span
              key={i}
              // The track is sand, not a tint of the text colour: this block's
              // background comes from the CMS and is the same dark green as
              // `primary` on the homepage, so `primary/20` painted green on
              // green and only the active segment was visible.
              className={`h-0.5 flex-1 transition-colors ${i === active ? 'bg-sea' : 'bg-sand'}`}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default ProductCardsRow;
